import { IQuery } from 'core-ts-types';
import { ReminderListDatastore, ReminderPriorityFeedQuery } from 'data-ng-personal-reminders';
import { StaffTodoListDatastore, StaffTodoFilterServant } from 'data-ng-staff-todos';
import {
  IContentTileActionObj,
  IContentTileExpectedObj,
  IContentTileIconObj,
  IContentTileTextObj
} from 'ui-ng-content-tile';
import { IDatasource, Query } from 'utility-ts-orm';
import { TypeUtility } from 'utility-ts-type';
import { TimeFormatterFunction } from '../functions/time_formatter_fn';
import { PathItem } from './../builders/path_item_builder';
import { PathTask } from './../builders/path_task_builder';
import { PathTaskFormatter } from './../functions/path_task_formatter_fn';

export class ToDoPriorityFeedQuery implements IQuery {
  private _result: IContentTileExpectedObj;
  private __hasMultipleTodos: boolean;
  private __isActive: boolean;
  private _count: number;

  constructor(
    private ngPathTaskFormatter: PathTaskFormatter,
    private ngPathItemDatastore: IDatasource<PathItem>,
    private ngPathTaskDatastore: IDatasource<PathTask>,
    private ngTypeUtility: typeof TypeUtility,
    private TaskStatusEnum: any,
    private EventCategoryEnum: any,
    private staffTodoListDatastore: StaffTodoListDatastore,
    private ngStaffTodoFilterServant: StaffTodoFilterServant,
    private ngReminderPriorityFeedQuery: ReminderPriorityFeedQuery,
    private reminderListDatastore: ReminderListDatastore
  ) {
    Query.registerQuery(
      this,
      this.ngPathItemDatastore,
      this.ngPathTaskDatastore,
      this.staffTodoListDatastore,
      this.reminderListDatastore
    );
  }

  run(): this {
    const icon = { type: 'apple_and_checkmark', theme: 'blue' };

    const todosForToday = this.ngPathTaskDatastore.query((task: PathTask) => this.__getActiveToDoForToday(task, false));
    const tasksForToday = this.ngPathTaskDatastore.query((task: PathTask) => this.__getActiveToDoForToday(task, true));
    const staffTodosForToday = this.ngStaffTodoFilterServant.staffTodosForToday();
    const remindersForTodayCount = this.ngReminderPriorityFeedQuery.count;
    const isOverdue = this.__containsOverdueToDos(todosForToday);
    const isActive = (this.__isActive = !_.isEmpty(todosForToday));

    this.__hasMultipleTodos = this.__isActive && todosForToday.length > 1;

    const text = this.__getToDoText(todosForToday);
    const action = this.__getToDoState(todosForToday);

    this._count = tasksForToday.length + staffTodosForToday.length + remindersForTodayCount;

    this._result = {
      icon,
      text,
      action,
      isOverdue,
      isActive
    };

    return this;
  }

  private __getActiveToDoForToday(task: PathTask, includeDatemarkers: boolean): boolean {
    const today = moment();
    const isTaskForToday =
      this.TaskStatusEnum.isActive(task.status) &&
      task.show &&
      task.mandatory &&
      !(this.EventCategoryEnum.isTip(task.category) || this.EventCategoryEnum.isPoll(task.category)) &&
      (task.start_ts.isBefore(today.endOf('day')) || task.end_ts.isBefore(today.endOf('day')));
    return includeDatemarkers ? isTaskForToday : isTaskForToday && !this.EventCategoryEnum.isDateMarker(task.category);
  }

  private __containsOverdueToDos(taskList: PathTask[]): boolean {
    const now: moment.Moment = moment();
    return !_.isEmpty(_.filter(taskList, (task: PathTask) => task.end_ts.isBefore(now)));
  }

  private __getToDoText(TaskList: PathTask[]): IContentTileTextObj {
    if (this.__hasMultipleTodos || !this.__isActive) {
      return {
        title: `You have ${TaskList.length} important to-dos for today`,
        subTitle: 'Check them out'
      };
    }

    const priorityToDoTask: PathTask = _.first(TaskList);
    const priorityToDoItem: PathItem = _.first(
      this.ngPathItemDatastore.query((item: PathItem) => item.id === priorityToDoTask.item)
    );

    return {
      title: this.ngPathTaskFormatter.getToDoTitle(priorityToDoTask, priorityToDoItem),
      subTitle: this.__getToDoDateTime(priorityToDoTask)
    };
  }

  private __getToDoDateTime(task: PathTask): string {
    const taskDue = task.end_ts;
    const noTime = taskDue.hours() === 0 && taskDue.minutes() === 0;
    const dueToday = moment().diff(taskDue, 'days') === 0;
    const sameEndDate = moment(task.start_ts).diff(task.end_ts, 'days') === 0;
    if (dueToday && noTime) {
      return 'Due today';
    }

    if (dueToday) {
      return `Due today at ${taskDue.format('h:mm a')}`;
    }

    if (noTime) {
      return `Due ${this.ngTypeUtility.time.getWeekDayMonthDay(taskDue)}`;
    }

    if (sameEndDate) {
      return `${TimeFormatterFunction.defaultTime(task.end_ts)}`;
    }

    return `Due ${this.ngTypeUtility.time.getWeekDayMonthDay(taskDue)} at ${taskDue.format('h:mm a')}`;
  }

  private __getToDoState(TaskList: PathTask[]): IContentTileActionObj {
    if (this.__hasMultipleTodos || !this.__isActive) {
      return {
        state: 'my.todos',
        params: {
          filterBy: 'todostoday'
        }
      };
    }

    const priorityToDoTask = _.first(TaskList);
    return {
      state: 'my.todos.details',
      params: {
        eventId: priorityToDoTask.event,
        repeatedBy: priorityToDoTask.repeated_by
      }
    };
  }

  get result() {
    return this._result;
  }

  get count() {
    return this._count;
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-todos/src/data/queries/to_do_priority_feed_query.ts